import { useState } from 'react';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from '@tanstack/react-router';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import CheckIcon from '@mui/icons-material/Check';

import { useCreateTurnReport } from '~/hooks/api/useCreateTurnReport';
import messages from './messages';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';

type TurnReportButtonProps = {
  turn: {
    id: number;
    turnReport?: {
      id: number;
      closed?: boolean;
    };
  };
  shift: {
    startDate: string;
  };
  submissionDeadlineExceeded: boolean;
};

export const TurnReportButton = (props: TurnReportButtonProps) => {
  const [deadlineDialogOpen, setDeadlineDialogOpen] = useState(false);
  const [notStartedDialogOpen, setNotStartedDialogOpen] = useState(false);
  const navigate = useNavigate();
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const visitTurnReport = (id: number) => {
    navigate({
      to: `/turn-reports/${id}`,
      search: { from: location.pathname },
    });
  };
  const createTurnReportMutation = useCreateTurnReport({
    onSuccess(turnReport) {
      visitTurnReport(turnReport.id!);
      showSnackbar({
        message: intl.formatMessage(messages.createTurnReportSuccess),
      });
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.createTurnReportFailed),
      });
    },
  });
  if (
    props.turn.turnReport &&
    // when the closed field is not defined, the turn report is already closed
    // and thus not visible to the turn leader anymore.
    (props.turn.turnReport.closed || props.turn.turnReport.closed === undefined)
  ) {
    return (
      <Button variant="contained" startIcon={<CheckIcon />} disabled>
        {intl.formatMessage(messages.closedTurnReport)}
      </Button>
    );
  }
  if (props.turn.turnReport) {
    return (
      <Button
        variant="contained"
        onClick={() => {
          visitTurnReport(props.turn.turnReport!.id);
        }}
      >
        {intl.formatMessage(messages.viewTurnReport)}
      </Button>
    );
  }
  if (props.submissionDeadlineExceeded) {
    return (
      <>
        <Dialog
          onClose={() => setDeadlineDialogOpen(false)}
          open={deadlineDialogOpen}
        >
          <DialogTitle>
            <FormattedMessage {...messages.reportCantBeCreatedAnymore} />
          </DialogTitle>
          <DialogContent>
            <FormattedMessage {...messages.contactPlanningTeam} />
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              onClick={() => setDeadlineDialogOpen(false)}
            >
              <FormattedMessage {...messages.ok} />
            </Button>
          </DialogActions>
        </Dialog>
        <Button variant="contained" onClick={() => setDeadlineDialogOpen(true)}>
          {intl.formatMessage(messages.createTurnReport)}
        </Button>
      </>
    );
  }
  const shiftHasStarted = moment().isAfter(props.shift.startDate);
  if (!shiftHasStarted) {
    return (
      <>
        <Dialog
          onClose={() => setNotStartedDialogOpen(false)}
          open={notStartedDialogOpen}
        >
          <DialogTitle>
            <FormattedMessage {...messages.reportCantBeCreatedYet} />
          </DialogTitle>
          <DialogContent>
            <FormattedMessage {...messages.shiftHasNotStartedYet} />
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              onClick={() => setNotStartedDialogOpen(false)}
            >
              <FormattedMessage {...messages.ok} />
            </Button>
          </DialogActions>
        </Dialog>
        <Button
          variant="contained"
          onClick={() => setNotStartedDialogOpen(true)}
        >
          {intl.formatMessage(messages.createTurnReport)}
        </Button>
      </>
    );
  }

  return (
    <Button
      variant="contained"
      onClick={() => createTurnReportMutation.mutate({ turnId: props.turn.id })}
      disabled={createTurnReportMutation.isPending}
    >
      {createTurnReportMutation.isPending
        ? intl.formatMessage(messages.isCreatingTurnReport)
        : intl.formatMessage(messages.createTurnReport)}
    </Button>
  );
};

export default TurnReportButton;
